import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { Check, Lock, Plus } from 'lucide-react';
import { FloorplanVersion } from '../../types/floorplan';
import { ConfirmDialog, SectionCard, StatusPill } from '../ui';
import { useDelayedFlag } from '../ui/useDelayedFlag';
import { sbg } from '../../theme/sbgTokens';
import { formatDateTime } from '../../utils/format';

interface VersionSelectorProps {
  versions: FloorplanVersion[];
  selectedVersionId: string | null;
  isCreating?: boolean;
  isActivating?: boolean;
  canManageVersions?: boolean;
  /** Optional: the versions are still loading (shows a skeleton instead of the empty state). */
  loading?: boolean;
  onSelect: (versionId: string) => void;
  onCreate: (payload: { name: string; description?: string; cloneFromVersionId?: string }) => Promise<void>;
  onActivate: (versionId: string) => Promise<void>;
}

const VersionStatus: React.FC<{ version: FloorplanVersion }> = ({ version }) =>
  version.isActive ? <StatusPill tone="success">Active</StatusPill> : <StatusPill tone="neutral">Draft</StatusPill>;

const VersionSelector: React.FC<VersionSelectorProps> = ({
  versions,
  selectedVersionId,
  isCreating = false,
  isActivating = false,
  canManageVersions = true,
  loading = false,
  onSelect,
  onCreate,
  onActivate,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [cloneCurrent, setCloneCurrent] = useState(true);
  // Versions arrive with the layout. Until then nothing here is known: no empty-state
  // copy, and no "New draft" (a draft started now couldn't copy the current seating).
  const initialLoading = loading && versions.length === 0;
  const showSkeleton = useDelayedFlag(initialLoading);

  const selectedVersion = useMemo(
    () => versions.find((version) => version.id === selectedVersionId) || null,
    [selectedVersionId, versions]
  );
  const activeVersion = useMemo(() => versions.find((version) => version.isActive) || null, [versions]);

  const handleCreate = async () => {
    if (!name.trim()) {
      return;
    }

    await onCreate({
      name: name.trim(),
      description: description.trim() || undefined,
      cloneFromVersionId: cloneCurrent ? selectedVersionId || undefined : undefined,
    });

    setDialogOpen(false);
    setName('');
    setDescription('');
    setCloneCurrent(true);
  };

  const handleActivate = async () => {
    if (!selectedVersionId) {
      return;
    }
    await onActivate(selectedVersionId);
    setConfirmOpen(false);
  };

  // "Make active" only exists when it can do something: a draft is selected.
  // On the active version the region has one quiet action, "New draft".
  const canActivateSelected = Boolean(selectedVersion && !selectedVersion.isActive);
  // With no versions yet, creating the first draft is the region's first action.
  const newDraftIsPrimary = versions.length === 0 && !loading;

  const renderBody = () => {
    if (versions.length === 0) {
      if (initialLoading) {
        return (
          <Box role="status" aria-label="Loading versions…">
            <span className="sbg-sr-only">Loading versions…</span>
            {/* Held but hidden for the first 300ms, so a quick load never flashes. */}
            <Box aria-hidden="true" sx={{ display: 'grid', gap: 1, visibility: showSkeleton ? 'visible' : 'hidden' }}>
              <Skeleton variant="rounded" height={36} />
              <Skeleton variant="rounded" width="60%" height={14} />
              {canManageVersions && <Skeleton variant="rounded" height={32} sx={{ borderRadius: sbg.radius.pill }} />}
            </Box>
          </Box>
        );
      }
      return (
        <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
          {canManageVersions
            ? 'No versions yet. Create a draft to start planning seats.'
            : 'No versions yet. An admin creates the first one.'}
        </Typography>
      );
    }

    return (
      <>
        <TextField
          select
          fullWidth
          label="Version"
          value={selectedVersionId || ''}
          onChange={(event) => onSelect(event.target.value)}
          SelectProps={{
            // The menu rows carry a date and status; the closed field shows the name alone.
            renderValue: (value) => versions.find((item) => item.id === value)?.name ?? '',
          }}
        >
          {versions.map((version) => (
            <MenuItem key={version.id} value={version.id} sx={{ alignItems: 'flex-start', py: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="labelMd" component="div" noWrap sx={{ color: sbg.text.primary }}>
                  {version.name}
                </Typography>
                <Typography variant="caption" component="div" sx={{ color: sbg.text.secondary }}>
                  {`Created ${formatDateTime(version.createdAt)}`}
                </Typography>
              </Box>
              <VersionStatus version={version} />
            </MenuItem>
          ))}
        </TextField>

        {selectedVersion && (
          <Box sx={{ display: 'grid', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <VersionStatus version={selectedVersion} />
              <Typography variant="caption" component="span" sx={{ color: sbg.text.secondary }}>
                {`Created ${formatDateTime(selectedVersion.createdAt)}`}
              </Typography>
            </Box>
            {selectedVersion.description && (
              <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, overflowWrap: 'anywhere' }}>
                {selectedVersion.description}
              </Typography>
            )}
            {!selectedVersion.isActive && (
              <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
                {activeVersion
                  ? `You’re viewing a draft. People see “${activeVersion.name}” until this one is made active.`
                  : 'You’re viewing a draft. Make it active to use its seating.'}
              </Typography>
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <SectionCard dense title="Seating version">
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {renderBody()}

        {canManageVersions && !initialLoading && (
          <Box sx={{ display: 'grid', gridTemplateColumns: canActivateSelected ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 1 }}>
            <Button
              variant={newDraftIsPrimary ? 'contained' : 'outlined'}
              size="small"
              startIcon={<Plus size={16} />}
              onClick={() => setDialogOpen(true)}
              disabled={isCreating}
              aria-busy={isCreating || undefined}
              fullWidth
            >
              {isCreating ? 'Creating…' : 'New draft'}
            </Button>
            {canActivateSelected && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Check size={16} />}
                onClick={() => setConfirmOpen(true)}
                disabled={isActivating}
                aria-busy={isActivating || undefined}
                fullWidth
              >
                {isActivating ? 'Activating…' : 'Make active'}
              </Button>
            )}
          </Box>
        )}
        {!canManageVersions && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: sbg.text.secondary }}>
            <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', mt: '1px' }}>
              <Lock size={14} />
            </Box>
            <Typography variant="body2" component="p" sx={{ color: 'inherit' }}>
              Only admins can create drafts or change the active version.
            </Typography>
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={confirmOpen && canManageVersions}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleActivate}
        title={selectedVersion ? `Make “${selectedVersion.name}” the active version?` : 'Make this the active version?'}
        body={
          activeVersion && activeVersion.id !== selectedVersionId
            ? `It replaces “${activeVersion.name}” as the seating plan people see when they open the floor plan.`
            : 'It becomes the seating plan people see when they open the floor plan.'
        }
        confirmLabel="Make active"
        loading={isActivating}
        loadingLabel="Activating…"
      />

      <Dialog
        open={dialogOpen && canManageVersions}
        onClose={() => { if (!isCreating) setDialogOpen(false); }}
        fullWidth
        maxWidth="sm"
        aria-labelledby="floorplan-version-create-title"
      >
        <Box
          component="form"
          noValidate
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            void handleCreate();
          }}
        >
          <DialogTitle id="floorplan-version-create-title">Create a draft version</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary }}>
                Plan seating changes in a draft. The active version stays as it is until you make the draft active.
              </Typography>
              <TextField
                autoFocus
                required
                label="Version name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Spring seating update"
                fullWidth
              />
              <TextField
                label="Description (optional)"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="e.g. Moves the Sales team to the second floor"
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
              />
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={cloneCurrent}
                      onChange={(event) => setCloneCurrent(event.target.checked)}
                      disabled={!selectedVersionId}
                    />
                  }
                  label={selectedVersion ? `Copy seating from “${selectedVersion.name}”` : 'Copy seating from the selected version'}
                />
                <FormHelperText sx={{ mt: 0, ml: 3.5 }}>
                  {selectedVersionId
                    ? 'Leave unchecked to start with every desk open.'
                    : 'Choose a version first to copy its seating.'}
                </FormHelperText>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={() => setDialogOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!name.trim() || isCreating} aria-busy={isCreating || undefined}>
              {isCreating ? 'Creating…' : 'Create draft'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </SectionCard>
  );
};

export default VersionSelector;
